import React, { useState } from "react";
import { Avatar, message, Tooltip } from "antd";
import { CloseOutlined, UsergroupAddOutlined, LoadingOutlined } from "@ant-design/icons";
import defaultProfileImage from "../../../assets/defult-profile-logo.png";
import { workwiseServices } from "../../../Api/api";
import { MindMapContext } from "../../../Context/contex";
import "./shareForm.css";
import "./peopleWithAccess.css";

const RIGHT_LABELS = { 1: "Reader", 2: "Collaborator" };
const READER_STATUS = 1;
const COLLABORATOR_STATUS = 2;
const COLLAPSED_COUNT = 3;
const AVATAR_SIZE = 28;

const EmptyState = () => (
    <div className="doc-member-container">
        <div className="doc-member-header">
            <p className="share-section-label">People with access</p>
        </div>
        <div className="doc-member-empty">
            <span className="doc-member-empty__icon">
                <UsergroupAddOutlined />
            </span>
            <span className="doc-member-empty__text">
                <span className="doc-member-empty__title">Not shared with anyone yet</span>
                <span className="doc-member-empty__hint">People you invite will appear here.</span>
            </span>
        </div>
    </div>
);

const MemberRow = ({ member, isRemoving, isBusy, onRemove }) => {
    const rightLabel = RIGHT_LABELS[member.rightType ?? member.memberRightType];
    return (
        <div className="member-row" data-removing={isRemoving ? "true" : undefined}>
            <div className="member-row__identity">
                <Avatar size={AVATAR_SIZE} src={member.image || defaultProfileImage} />
                <div className="member-row__info">
                    <span className="member-row__name" title={member.name}>{member.name}</span>
                    {member.designation && (
                        <span className="member-row__designation" title={member.designation}>{member.designation}</span>
                    )}
                </div>
            </div>
            <div className="member-row__meta">
                {rightLabel && <span className="member-row__right">{rightLabel}</span>}
                <Tooltip title={isRemoving ? "Removing…" : "Remove access"} placement="left">
                    <button
                        type="button"
                        className="member-row__remove"
                        aria-label={`Remove ${member.name}`}
                        aria-busy={isRemoving}
                        disabled={isBusy}
                        onClick={() => onRemove(member)}
                    >
                        {isRemoving ? <LoadingOutlined /> : <CloseOutlined />}
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

/** Everyone else on the map, with their right and a remove button; the first three until expanded. */
const PeopleWithAccess = () => {
    const { documentInfo, setDocumentInfo, userInfo } = MindMapContext();
    const [showAll, setShowAll] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const handleRemoveMember = async (member) => {
        if (removingId) return;
        setRemovingId(member.id);
        try {
            // A member holds a reader or a collaborator right; both are removed.
            await workwiseServices.removeDocumentMember({ documentId: documentInfo?.referenceId, status: READER_STATUS, documentMembers: [member.id] });
            await workwiseServices.removeDocumentMember({ documentId: documentInfo?.referenceId, status: COLLABORATOR_STATUS, documentMembers: [member.id] });
            setDocumentInfo({ ...documentInfo, members: documentInfo.members.filter((m) => m.id !== member.id) });
        } catch (error) {
            console.error("Error removing document member:", error);
            message.error(error.response?.data?.message || "Failed to remove member.");
        } finally {
            setRemovingId(null);
        }
    };

    const members = documentInfo?.members?.filter((member) => member.id !== userInfo?.userId) || [];
    if (members.length === 0) return <EmptyState />;

    const visibleMembers = showAll ? members : members.slice(0, COLLAPSED_COUNT);

    return (
        <div className="doc-member-container">
            <div className="doc-member-header">
                <p className="share-section-label">People with access</p>
                <span className="doc-member-count">{members.length}</span>
            </div>
            <div className="doc-member-list">
                {visibleMembers.map((member) => (
                    <MemberRow
                        key={member.id}
                        member={member}
                        isRemoving={removingId === member.id}
                        isBusy={Boolean(removingId)}
                        onRemove={handleRemoveMember}
                    />
                ))}
                {members.length > COLLAPSED_COUNT && (
                    <button type="button" className="member-toggle-all" onClick={() => setShowAll(!showAll)}>
                        {showAll ? "Show less" : `View all (${members.length})`}
                    </button>
                )}
            </div>
        </div>
    );
};

export default PeopleWithAccess;
